<template>
  <div>
    <v-btn id="split_secret_back_btn" icon @click="backPage">
      <v-icon>chevron_left</v-icon>
    </v-btn>

    <v-row no-gutters align="center">
      <span class="text-h6 context_title_color--text">
        {{ $tc('split_secret') }}
      </span>

      <span class="text-h5 font-weight-medium mx-1">
        {{ '/' }}
      </span>

      <card-toolbar :title="name" class="mt-md-2" />
    </v-row>

    <div>
      <v-row class="mb-5">
        <v-col cols="12" md="6">
          <v-text-field
            :label="$tc('n_of_split')"
            type="number"
            hide-spin-buttons
            v-model="nSplit"
            :error-messages="nSplitErrors"
            @input="$v.nSplit.$touch()"
            @blur="$v.nSplit.$touch()"
            append-outer-icon="add"
            @click:append-outer="incrementN"
            prepend-icon="remove"
            @click:prepend="decrementN"
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            :label="$tc('m_of_split')"
            type="number"
            hide-spin-buttons
            v-model="mSplit"
            :error-messages="mSplitErrors"
            @input="$v.mSplit.$touch()"
            @blur="$v.mSplit.$touch()"
            append-outer-icon="add"
            @click:append-outer="incrementM"
            prepend-icon="remove"
            @click:prepend="decrementM"
          />
        </v-col>

        <v-col cols="12">
          <v-row justify="end">
            <v-btn
              id="select_secret_save"
              color="blue"
              :disabled="$v.$invalid || shareList.length > 0"
              :dark="!$v.$invalid && !(shareList.length > 0)"
              @click="submit"
            >
              {{ $tc('create') }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </div>

    <div>
      <div v-if="loading">
        <v-row align-content="center">
          <v-progress-circular
            :size="100"
            indeterminate
            class="mx-auto my-12"
          />
        </v-row>
      </div>

      <div v-else-if="shareList.length > 0">
        <v-stepper
          elevation="0"
          style="background: transparent;"
          vertical
          v-model="activeStep"
        >
          <template v-for="index in steps">
            <v-stepper-step
              :key="`${index}-step`"
              :complete="activeStep > index"
              :step="index"
            >
              <span v-if="index % 2 === 0">
                {{ $tc("part") + " " + (index / 2) }}
              </span>
            </v-stepper-step>

            <v-stepper-content
              :key="`${index}-content`"
              :step="index"
            >
              <v-card
                flat
                outlined
                style="background: transparent;"
                class="pa-3"
                rounded="lg"
              >
                <div v-if="index % 2 !== 0">
                  <v-alert
                    v-if="index === 1"
                    style="background: transparent;"
                    border="left"
                    colored-border
                    type="warning"
                  >
                    {{
                      $t("split_secret_warning", {
                        num: mSplit,
                      })
                    }}
                  </v-alert>

                  <v-alert
                    style="background: transparent;"
                    border="left"
                    colored-border
                    type="warning"
                  >
                    {{
                      $t("next_split_warning", {
                        num: ((index + 1) / 2),
                      })
                    }}
                  </v-alert>
                </div>

                <div v-else>
                  <v-row align="center">
                    <v-col cols="12" md="6">
                      <v-text-field
                        :id="`stepper_${index}`"
                        class="monospace"
                        :label="$tc('shadow') + ' ' + (index / 2)"
                        readonly
                        :value="shareList[(index / 2) - 1].shadow"
                      />

                      <v-text-field
                        :id="`stepper_${index}`"
                        class="monospace"
                        :label="$tc('shadow_cks')"
                        readonly
                        :value="shareList[(index / 2) - 1].cks"
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <template>
                        <v-row no-gutters justify="center">
                          <v-card
                            rounded="lg"
                            elevation="0"
                            light
                            class="pa-2"
                          >
                            <qrcode :value="`${shareList[(index / 2) - 1].shadow} ${shareList[(index / 2) - 1].cks}`" :options="{ size: 200 }" />
                          </v-card>
                        </v-row>
                      </template>
                    </v-col>
                  </v-row>
                </div>
              </v-card>

              <v-row
                no-gutters
                class="mt-4 mb-2 my-2"
                justify="end"
              >
                <v-btn
                  color="blue"
                  dark
                  @click="nextStep(index)"
                >
                  <span v-if="index !== steps">

                    {{ $tc("next") }}
                  </span>

                  <span v-else>
                    {{ $tc("ok") }}
                  </span>
                </v-btn>
              </v-row>
            </v-stepper-content>
          </template>
        </v-stepper>
      </div>
    </div>
  </div>
</template>

<script>
import CardToolbar from '@/components/CardToolbar'
import { required } from 'vuelidate/lib/validators'
import VueQrcode from '@chenfengyuan/vue-qrcode'

const MAX_N = 64
const MIN_N = 2
const MIN_M = 2

export default {
  components: {
    qrcode: VueQrcode,
    CardToolbar
  },
  data () {
    return {
      item: null,
      name: null,
      mSplit: 2,
      nSplit: 2,
      loading: false,
      activeStep: 1,
      steps: 1,
      shareList: [],
      page: 0
    }
  },
  created () {
    this.item = this.$route.params.item
    this.name = this.$route.params.item.csp
    this.page = this.$route.params.page
  },
  computed: {
    mSplitErrors () {
      const errors = []
      if (!this.$v.mSplit.$dirty) return errors
      !this.$v.mSplit.required &&
        errors.push(this.$t('$validation.required'))
      !this.$v.mSplit.positiveValue &&
        errors.push(this.$t('$validation.m_positive_value'))
      !this.$v.mSplit.isSmallerThanOrEqualToN &&
        errors.push(this.$t('$validation.is_smaller_than_n'))
      return errors
    },
    nSplitErrors () {
      const errors = []
      if (!this.$v.nSplit.$dirty) return errors
      !this.$v.nSplit.required &&
        errors.push(this.$t('$validation.required'))
      !this.$v.nSplit.positiveValue &&
        errors.push(this.$t('$validation.n_positive_value'))
      !this.$v.nSplit.isGreaterThanOrEqualToM &&
        errors.push(this.$t('$validation.is_greater_than_or_equal_to_m'))
      return errors
    }
  },
  validations () {
    const validation = {}

    const positiveValue = value => {
      const number = Number(value)
      return Number.isInteger(number) && number >= 2 && number <= MAX_N
    }

    const isSmallerThanOrEqualToN = (value) => {
      const numberValue = Number(value)
      const numberN = Number(this.nSplit)
      return numberValue <= numberN
    }

    const isGreaterThanOrEqualToM = (value) => {
      const numberValue = Number(value)
      const numberM = Number(this.mSplit)
      return numberValue >= numberM
    }

    validation.mSplit = {
      required,
      positiveValue,
      isSmallerThanOrEqualToN
    }

    validation.nSplit = {
      required,
      positiveValue,
      isGreaterThanOrEqualToM
    }

    return validation
  },
  methods: {
    backPage () {
      const safeKeepingTab = 0

      if (this.page > 0 && this.page !== undefined) {
        this.$router.push(
          {
            name: 'partition-keys',
            query: {
              p: this.page
            }
          }
        )
      } else {
        this.$router.push(
          {
            name: 'hsm-tools',
            params: {
              tabOption: safeKeepingTab
            }
          }
        )
      }
    },
    incrementN () {
      if (this.nSplit < MAX_N) {
        this.nSplit = parseInt(this.nSplit, 10) + 1
      }
    },
    decrementN () {
      if (this.nSplit > MIN_N) {
        this.nSplit = parseInt(this.nSplit, 10) - 1
      }
    },
    incrementM () {
      if (this.mSplit < this.nSplit) {
        this.mSplit = parseInt(this.mSplit, 10) + 1
      }
    },
    decrementM () {
      if (this.mSplit > MIN_M) {
        this.mSplit = parseInt(this.mSplit, 10) - 1
      }
    },
    nextStep (n) {
      const steps = this.steps - 1
      if (n === steps + 1) {
        this.backPage()
      } else {
        this.activeStep = n + 1
      }
    },
    async submit () {
      const m = Number(this.mSplit)
      const n = Number(this.nSplit)
      this.loading = true

      try {
        const res = await this.$store.dispatch('hsm/skeep_split_secret', {
          body: {
            obj: this.item.csp,
            type: this.item.type,
            sec_level: this.item.secLevel,
            m,
            n
          }
        })

        this.$notify.success({})
        this.shareList = await Promise.all(
          res.share.map(async shadow => {
            const cks = await this.skeepShadowCks(shadow)
            return { shadow, cks }
          })
        )
        this.steps = 2 * res.share.length
      } catch (err) {
        const errObj = this.$store.getters['error/getErrorObj'](err)
        if (errObj) {
          this.$notify.hsmError(errObj, err)
        } else {
          this.$notify.error({ message: err })
        }
      } finally {
        this.loading = false
      }
    },
    async  skeepShadowCks (s) {
      const m = new TextEncoder().encode(s)
      const b = await crypto.subtle.digest('SHA-256', m)
      const v = new DataView(b)

      const PRIME = 9973
      const RNDI = v.getUint32(0, false)
      const CKS = RNDI % PRIME

      return CKS.toString().padStart(4, '0')
    }
  },
  watch: {
    steps (val) {
      if (this.activeStep > val) {
        this.activeStep = val
      }
    }
  }
}
</script>
